import React from "react";
import { TableSearchColumn, tableSearchColumnsDefaults } from "./utils";

type ColumnSizerRenderProps = {
  getColumnWidth: (dataKey: string) => number;
  ref: ColumnSizer;
};

type Props = {
  children: (renderProps: ColumnSizerRenderProps) => React.ReactNode;
  columns: Array<TableSearchColumn<any>>;
  width: number;
};

type State = {
  columnWidths: Array<{
    dataKey: string;
    width: number;
  }>;
  isLoading: boolean;
};

class ColumnSizer extends React.Component<Props, State> {
  static getDerivedStateFromProps(props: Props) {
    const { width } = props;

    if (typeof width !== "number" || width <= 0) {
      return { columnWidths: [], isLoading: true };
    }

    const columnWidths = getColumnsWidths(
      width,
      props.columns
        .filter((x) => !x.skip)
        .map((x) => ({
          ...tableSearchColumnsDefaults,
          ...x
        }))
    );

    return { columnWidths, isLoading: false };
  }

  state: State = {
    columnWidths: [],
    isLoading: true
  };

  getColumnWidth = (dataKey: string) => {
    const column = this.state.columnWidths.find((x) => x.dataKey === dataKey);

    if (!column) {
      throw new Error(`cannot find column "${dataKey}"!`);
    }

    return column.width;
  };

  getTotalWidth = () => this.props.width;

  render() {
    if (this.state.isLoading) {
      return null;
    }

    return this.props.children({
      getColumnWidth: this.getColumnWidth,
      ref: this
    });
  }
}

export default ColumnSizer;

/**
 * Redistributes free space between columns based on fixed width and
 * floating width
 */
function getColumnsWidths(width: number, columns: Array<TableSearchColumn<any>>) {
  // get full width
  // subtract static columns
  // distribute the rest

  const totalStaticWidth = columns.reduce(
    (p, c) => (typeof c.width === "number" ? p + c.width : p),
    0
  );

  const totalFreeWidth = width - totalStaticWidth;
  const totalFrs = columns.reduce(
    (p, c) => (c.width === "auto" ? p + c.widthProportion : p),
    0
  );
  const frWidth = totalFrs > 0 ? totalFreeWidth / totalFrs : 0;

  if (totalFreeWidth <= 0) {
    // handle this somehow..
  }

  return columns.map((x) => ({
    dataKey: x.dataKey,
    width: typeof x.width === "number" ? x.width : x.widthProportion * frWidth
  }));
}
